import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  Select,
  Option,
  Checkbox,
  Alert,
  CircularProgress,
  Slider,
  Grid,
  Divider,
} from '@mui/joy'
import {
  ArrowLeft as ArrowBackIcon,
  CloudArrowUp as CloudUploadIcon,
  MagnifyingGlass as SearchIcon,
} from '@phosphor-icons/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { SearchableSelect } from '../../components/SearchableSelect'

interface Job {
  id: number
  name: string
  address: string
  status: string
}

interface Photo {
  id: number
  name: string
  thumbnail_url: string
  created_at: string
  job: number
}

interface AnalysisOptions {
  photo_id?: number
  job_id?: number
  use_tiling: boolean
  run_anomaly_detection: boolean
  run_open_set: boolean
  confidence_threshold: number
  model_version?: string
}

export const AnalysisSubmit: React.FC = () => {
  const navigate = useNavigate()
  const [selectedJob, setSelectedJob] = useState<number | null>(null)
  const [selectedPhoto, setSelectedPhoto] = useState<number | null>(null)
  const [options, setOptions] = useState<AnalysisOptions>({
    use_tiling: true,
    run_anomaly_detection: false,
    run_open_set: false,
    confidence_threshold: 0.5,
  })

  // Fetch jobs
  const { data: jobs = [] } = useQuery({
    queryKey: ['jobs'],
    queryFn: async () => {
      const response = await api.get<{ results: Job[] }>('/api/jobs/')
      return response.data.results
    },
  })

  // Fetch photos for selected job
  const { data: photos = [], isLoading: photosLoading } = useQuery({
    queryKey: ['photos', selectedJob],
    queryFn: async () => {
      if (!selectedJob) return []
      const response = await api.get<{ results: Photo[] }>(`/api/photos/?job=${selectedJob}`)
      return response.data.results
    },
    enabled: !!selectedJob,
  })

  // Submit analysis mutation
  const submitAnalysis = useMutation({
    mutationFn: async (data: AnalysisOptions) => {
      const response = await api.post('/api/hawkeye/analyses/analyze/', data)
      return response.data as any
    },
    onSuccess: (data) => {
      // Navigate to analysis detail page
      navigate(`/analysis/${(data as any)?.id || ''}`)
    },
  })

  const handleSubmit = () => {
    if (!selectedPhoto) return

    submitAnalysis.mutate({
      ...options,
      photo_id: selectedPhoto,
      job_id: selectedJob || undefined,
    })
  }

  const jobOptions = jobs.map((job: any) => ({
    value: job.id,
    label: job.name,
    subtitle: job.address,
  }))

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          variant="plain"
          startDecorator={<ArrowBackIcon />}
          onClick={() => navigate('/')}
        >
          Back
        </Button>
        <Typography level="h2">New Analysis</Typography>
      </Box>

      {submitAnalysis.error && (
        <Alert color="danger" sx={{ mb: 3 }}>
          {(submitAnalysis.error as any)?.message || 'Failed to submit analysis'}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Image Selection */}
        <Grid xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography level="title-lg" sx={{ mb: 3 }}>Select Image</Typography>

              {/* Job Selection */}
              <FormControl sx={{ mb: 3 }}>
                <FormLabel>Job (Optional)</FormLabel>
                <SearchableSelect
                  options={jobOptions}
                  value={selectedJob}
                  onChange={(value) => setSelectedJob(value as number | null)}
                  placeholder="Search for a job..."
                  startDecorator={<SearchIcon />}
                />
              </FormControl>

              {/* Photo Selection */}
              {selectedJob && (
                <FormControl>
                  <FormLabel>Photo</FormLabel>
                  {photosLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : photos.length > 0 ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 2 }}>
                      {photos.map((photo: any) => (
                        <Box
                          key={photo.id}
                          sx={{
                            cursor: 'pointer',
                            borderRadius: 'sm',
                            overflow: 'hidden',
                            border: '2px solid',
                            borderColor: selectedPhoto === photo.id ? 'primary.main' : 'transparent',
                            '&:hover': {
                              borderColor: 'primary.light',
                            },
                          }}
                          onClick={() => setSelectedPhoto(photo.id)}
                        >
                          <img
                            src={photo.thumbnail_url}
                            alt={photo.name}
                            style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                          />
                          <Box sx={{ p: 1 }}>
                            <Typography level="body-xs" noWrap>{photo.name}</Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Alert color="warning">No photos found for this job</Alert>
                  )}
                </FormControl>
              )}

              {/* Upload Option */}
              <Divider sx={{ my: 3 }}>OR</Divider>
              <Box sx={{ textAlign: 'center' }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startDecorator={<CloudUploadIcon />}
                  disabled
                >
                  Upload Image (Coming Soon)
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Analysis Options */}
        <Grid xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography level="title-lg" sx={{ mb: 3 }}>Analysis Options</Typography>

              <FormControl sx={{ mb: 3 }}>
                <FormLabel>Model Version</FormLabel>
                <Select
                  value={options.model_version || 'default'}
                  onChange={(_, value) => setOptions({ ...options, model_version: value as string })}
                >
                  <Option value="default">Default (YOLOv8)</Option>
                  <Option value="yolov8n-roof">YOLOv8n Roof</Option>
                  <Option value="yolov8s-roof">YOLOv8s Roof</Option>
                  <Option value="yolov10n-roof">YOLOv10n Roof</Option>
                </Select>
              </FormControl>

              <FormControl sx={{ mb: 3 }}>
                <FormLabel>Confidence Threshold</FormLabel>
                <Slider
                  value={options.confidence_threshold}
                  onChange={(_, value) => setOptions({ ...options, confidence_threshold: value as number })}
                  min={0}
                  max={1}
                  step={0.05}
                  valueLabelDisplay="auto"
                  marks={[
                    { value: 0, label: '0' },
                    { value: 0.5, label: '0.5' },
                    { value: 1, label: '1' },
                  ]}
                />
              </FormControl>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Checkbox
                  label="Use Tiled Inference (SAHI)"
                  checked={options.use_tiling}
                  onChange={(e) => setOptions({ ...options, use_tiling: e.target.checked })}
                />
                <Checkbox
                  label="Run Anomaly Detection"
                  checked={options.run_anomaly_detection}
                  onChange={(e) => setOptions({ ...options, run_anomaly_detection: e.target.checked })}
                />
                <Checkbox
                  label="Run Open-Set Detection"
                  checked={options.run_open_set}
                  onChange={(e) => setOptions({ ...options, run_open_set: e.target.checked })}
                />
              </Box>

              <Divider sx={{ my: 3 }} />

              <Button
                fullWidth
                size="lg"
                disabled={!selectedPhoto || submitAnalysis.isPending}
                loading={submitAnalysis.isPending}
                onClick={handleSubmit}
              >
                Start Analysis
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}